<template>
    <div class="page-platform" :style="{'background-image': `url(${platform.background[isWebpSupported ? 'webp' : 'jpg']})`}" v-if="platform">
        <v-container>
            <platform-item-description :platform-item="platform" v-if="platform"/>

            <div class="section-listGame__header">
                <div class="title">
                    <picture>
                        <source :srcset="platform.icon[sourceType]" :type="`image/${sourceType}`"
                                v-for="(sourceType, index) in platform.icon.sourcesTypes" :key="index">
                        <img :src="platform.icon.jpg || defaultPlatformIcon" :alt="platform.title"/>
                    </picture>
                    <div class="title__text">
                        <h2 v-text="platform.title"/>
                    </div>
                </div>

                <div class="link" v-if="!showAllGamesMode">
                    <router-link :to="allGamesUrl">
                        <span>{{ $t('front.game.button.see_all') }} {{ totalGamesCount }}</span>
                        <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 11L6 6L1 1" stroke="#926D20"/>
                        </svg>
                    </router-link>
                </div>
            </div>

            <component :is="component" :items="getComponentItems(index)" v-for="(component, index) in cycleComponents"
                       :key="index"></component>

            <div class="btn-loadCatalog" @click="showMoreGames()" v-if="!showAllGamesMode && hasMoreGames">
                <span>{{ $t('front.game.button.load_more') }}</span>
            </div>

        </v-container>
    </div>
</template>

<script>
import {brandName, appUrl} from "../../../../../config/app";
import {fetchPlatformGames} from "../../../../api";
import screenResolution from "../../../../mixins/screenResolution";
import {loadMoreGamesBlocksCount} from "../../../../../config/game";
import apiData from "../../../../mixins/apiData";
import sortGame from "../../../../mixins/sortGame";
import {platformGameSortOptions} from "../../../../interfaces/sortGameInterface";

export default {
    name: "PlatformItemContent",

    mixins: [screenResolution, apiData, sortGame],

    data: function () {
        return {
            hasMoreGames: true,
            screenWidth: undefined,
            loadMorePlatformGames: [],
            moreGamesLoading: false,
            cycleComponents: [
                'GamesBlockWithBigLeftItem',
                'GamesBlockWithBigRightItem',
            ],
            sortedOptionValue: undefined,
            sortOptions: this.getSortOptions(platformGameSortOptions),
        };
    },

    metaInfo() {
        let metaData = {};

        if (this.showAllGamesMode) {
            let query = Object.assign({}, this.$route.query);
            delete query.show;

            let canonicalRoute = this.$router.resolve({
                query: query,
            });

            metaData.link = [
                {
                    rel: 'canonical',
                    href: `${appUrl}${decodeURIComponent(canonicalRoute.resolved.path)}`,
                    vmid: 'canonical',
                }
            ];
        }

        return metaData;
    },

    serverPrefetch() {
        return this.fetchData();
    },

    mounted() {
        this.defineScreenWidth();
        window.addEventListener('resize', this.defineScreenWidth);

        this.fetchData();
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.defineScreenWidth);
    },

    watch: {
        showAllGamesMode() {
            this.resetData();
            this.fetchData();
        },

        user(user, oldUser) {
            if (user && !oldUser || oldUser && !user) {
                this.resetData();
                this.fetchData();
            }
        }
    },

    computed: {
        isWebpSupported() {
            return this.$store.state.isWebpSupported;
        },

        user() {
            return this.$store.state.user;
        },

        isDeviceMobile() {
            return this.$store.state.isDeviceMobile;
        },

        platform() {
            return this.$store.state.gamePlatformItem[this.platformSlug];
        },

        gamesData() {
            return this.platform && this.$store.state.platformGames[this.platform.id];
        },

        platformGames() {
            return this.gamesData && this.gamesData.games.concat(this.loadMorePlatformGames) || [];
        },

        totalGamesCount() {
            return this.gamesData && this.gamesData.totalGamesCount || 0;
        },

        showAllGamesMode() {
            return this.$route.query.show === 'all';
        },

        allGamesUrl() {
            let query = Object.assign({}, this.$route.query);
            query.show = 'all';

            let seeAllRoute = this.$router.resolve({
                query: query,
            });

            return decodeURIComponent(seeAllRoute.resolved.fullPath);
        },

        loadMoreBlocksCount() {
            let screenResolutionName = this.getCurrentScreenResolutionName(this.screenWidth);
            return loadMoreGamesBlocksCount[screenResolutionName] || 3;
        },

        locale() {
            return this.$store.state.locale;
        },

        platformSlug() {
            return this.$route.params.slug;
        },

        defaultPlatformIcon() {
            return `/public/${brandName}/platform/default_platform_icon.svg`;
        },

        sortedPlatformGames() {
            if (this.sortedOptionValue) {
                return this.sortGamesBySortOption(this.platformGames, this.sortedOptionValue);
            } else {
                return this.platformGames;
            }
        },

        cycleComponentGamesCount() {
            // for server side
            if (!this.screenWidth) {
                return this.isDeviceMobile ? 6 : 8;
            }

            if (this.screenWidth > 1024) {
                return 7;
            } else if (this.screenWidth > 767) {
                return 8;
            } else if (this.screenWidth > 600) {
                return 6;
            } else {
                return 4;
            }
        },

        maxCycleComponentGamesCount() {
            return 8;
        },

        startGamesCount() {
            // fetch max games count of possible needing for one cycle component
            return this.cycleComponents.length * this.maxCycleComponentGamesCount;
        },

        loadMoreGamesCount() {
            return (this.cycleComponents.length + this.loadMoreBlocksCount) * this.cycleComponentGamesCount - this.platformGames.length;
        },
    },

    methods: {
        resetData() {
            this.loadMorePlatformGames = [];
            this.cycleComponents = this.cycleComponents.slice(0, 2);
        },

        fetchData() {
            return new Promise(((resolve, reject) => {
                this.$store.dispatch('FETCH_GAME_PLATFORM_ITEM', this.createApiData({
                    slug: this.platformSlug,
                }))
                    .then(platform => {
                        if (platform) {
                            this.$store.dispatch('FETCH_PLATFORM_GAMES_BY_ID', this.createApiData({
                                platformId: this.platform.id,
                                offset: 0,
                                count: this.showAllGamesMode ? null : this.startGamesCount,
                            }))
                                .then(() => {
                                    this.showAllGamesMode && this.addCycleComponents();
                                    this.hasMoreGames = this.gamesData && this.gamesData.hasMoreGames;

                                    resolve();
                                })
                                .catch(error => reject(error));
                        } else {
                            resolve();
                        }
                    })
                    .catch(error => reject(error));
            }));
        },

        getComponentItems(LoopIndex) {
            let startItem = LoopIndex * this.cycleComponentGamesCount;
            let endItem = (LoopIndex + 1) * this.cycleComponentGamesCount;

            return this.sortedPlatformGames.slice(startItem, endItem);
        },

        showMoreGames() {
            if (this.moreGamesLoading) {
                return;
            }

            this.moreGamesLoading = true;

            fetchPlatformGames(this.createApiData({
                platformId: this.platform.id,
                count: this.loadMoreGamesCount,
                offset: this.platformGames.length,
            }))
                .then(gamesData => {
                    this.loadMorePlatformGames = this.loadMorePlatformGames.concat(gamesData.games);
                    this.addCycleComponents(this.loadMoreBlocksCount);
                    this.hasMoreGames = gamesData.hasMoreGames;
                    this.moreGamesLoading = false;
                })
                .catch(error => {
                    this.moreGamesLoading = false;
                    console.log(error)
                });
        },

        missingCycleComponentsCount() {
            let existingBlocksGamesCount = this.cycleComponentGamesCount * this.cycleComponents.length;
            return Math.ceil((this.platformGames.length - existingBlocksGamesCount) / this.cycleComponentGamesCount);
        },

        addCycleComponents(blocksCount) {
            let count = blocksCount || this.missingCycleComponentsCount();

            let oddGamesBlockComponentName = 'GamesBlockWithBigLeftItem';
            let evenGamesBlockComponentName = 'GamesBlockWithBigRightItem';

            for (let i = 0; i < count; i++) {
                let blockComponentName = this.cycleComponents.length % 2 === 0 ? oddGamesBlockComponentName : evenGamesBlockComponentName;
                this.cycleComponents.push(blockComponentName);
            }
        },

        sortGames(sortOptionValue) {
            this.sortedOptionValue = sortOptionValue;
        },

        defineScreenWidth() {
            this.screenWidth = window.innerWidth;
        },
    },
}
</script>

<style lang="sass" scoped>
@import "sass/components/pages/parts/target/PlatformItemContent"
</style>